import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Badge,
  Button,
  Dropdown,
  Space,
  Select,
  Tooltip,
  Avatar,
  Switch,
  Spin,
  Empty,
} from 'antd';
import {
  BellOutlined,
  MessageOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SunOutlined,
  MoonOutlined,
  AppstoreAddOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@features/auth/store/authStore';
import { authApi } from '@features/auth/api/authApi';
import { useUIStore } from '@shared/store/uiStore';
import { useNotifications } from '@features/header/hooks/useNotifications';
import { useMessages } from '@features/header/hooks/useMessages';
import { useCustomPages } from '@features/header/hooks/useCustomPages';
import { useRegions } from '@features/header/hooks/useRegions';
import { useWebSocketContext } from '@shared/providers/WebSocketContext';
import { SelfPasswordModal } from '@features/auth/components/SelfPasswordModal';
import { GlobalSearch } from './GlobalSearch';
import { QuickActions } from './QuickActions';
import type { MenuProps } from 'antd';
import { useTheme } from '@theme/useTheme';
import { isSupportedLanguage, switchLanguage } from '@i18n/config';

interface AppHeaderProps {
  collapsed: boolean;
  onCollapse: () => void;
  isMobile?: boolean;
  onMobileMenuToggle?: () => void;
}

export function AppHeader({ collapsed, onCollapse, isMobile = false, onMobileMenuToggle }: AppHeaderProps) {
  const { t, i18n } = useTranslation(['translation', 'auth']);
  const { theme: themeContext } = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const theme = useUIStore((state) => state.theme);
  const setTheme = useUIStore((state) => state.setTheme);
  const { isConnected } = useWebSocketContext();
  const {
    notifications,
    unreadCount: notificationsUnreadCount,
    isLoading: notificationsLoading,
    error: notificationsError,
    markAsRead,
  } = useNotifications();
  const {
    messages,
    unreadCount: messagesUnreadCount,
    isLoading: messagesLoading,
    error: messagesError,
  } = useMessages();
  const {
    pages: customPages,
    isLoading: customPagesLoading,
    error: customPagesError,
  } = useCustomPages('TOP_MENU');
  const {
    regions,
    currentRegionId,
    isLoading: regionsLoading,
    error: regionsError,
    changeRegion,
  } = useRegions();

  const handleLogout = async () => {
    try {
      await authApi.logout();
      logout();
      navigate('/login');
    } catch (error) {
      logout();
      navigate('/login');
    }
  };

  const handleThemeChange = (checked: boolean) => {
    const newTheme = checked ? 'dark' : 'light';
    setTheme(newTheme);
    // TODO: Сохранить выбор темы в профиле пользователя через API
  };

  const handleLanguageChange = (lang: string) => {
    if (isSupportedLanguage(lang)) {
      void switchLanguage(lang);
    }
    // TODO: Сохранить выбор языка в профиле пользователя
  };

  const handleNotificationClick = (notificationId: number, url?: string) => {
    markAsRead(notificationId);
    if (url) {
      navigate(url);
    }
  };

  const handleMessageClick = (customerId: number) => {
    if (customerId) {
      navigate(`/customers/${customerId}?tab=messages`);
    }
  };

  const buildErrorMenuItems = (
    scope: 'notifications' | 'messages' | 'regions' | 'customPages',
    error: ReturnType<typeof useNotifications>['error'],
  ): MenuProps['items'] | null => {
    if (!error) {
      return null;
    }
    const isAuthError = error.type === 'auth';
    const titleKey = isAuthError ? 'appHeader.loadError.authTitle' : 'appHeader.loadError.genericTitle';
    const descriptionKey = isAuthError
      ? 'appHeader.loadError.authDescription'
      : 'appHeader.loadError.genericDescription';

    return [
        {
        key: `${scope}-error`,
        disabled: true,
        label: (
          <div className="py-4 px-3 text-center space-y-2">
            <div className="font-semibold">{t(titleKey)}</div>
            <div className="text-sm text-gray-500">
              {t(descriptionKey, { status: error.status ?? '—' })}
            </div>
            {isAuthError && (
              <Button size="small" type="primary" onClick={() => navigate('/login')}>
                {t('auth:login')}
              </Button>
            )}
          </div>
        ),
      },
    ];
  };

  const notificationErrorItems = buildErrorMenuItems('notifications', notificationsError);
  const notificationItems: MenuProps['items'] = notificationsLoading
    ? [
        {
          key: 'notifications-loading',
          disabled: true,
          label: (
            <div className="py-4 text-center">
              <Spin size="small" />
            </div>
          ),
        },
      ]
    : notificationErrorItems
      ? notificationErrorItems
      : notifications.length > 0
        ? [
            ...notifications.map((notification) => ({
              key: `notification-${notification.id}`,
              onClick: () => handleNotificationClick(notification.id, notification.url),
              label: (
                <div className="py-2">
                  <div className="font-semibold">{notification.title}</div>
                  <div className="text-sm text-gray-600">{notification.message}</div>
                  <div className="text-xs text-gray-500">
                    {dayjs(notification.created_at).format('DD.MM.YYYY HH:mm')}
                  </div>
                </div>
              ),
            })),
            // Пункт «Показать все уведомления» УБРАН: экрана `/notifications` в
            // приложении нет вовсе, и переход доезжал до `<Route path="*">`, то есть
            // уводил оператора на главную. Пункт вернётся вместе с экраном.
          ]
        : [
            {
              key: 'notifications-empty',
              disabled: true,
              label: (
                <div className="py-4">
                  <Empty
                    description={t('appHeader.notifications.empty')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              ),
            },
          ];

  const messagesErrorItems = buildErrorMenuItems('messages', messagesError);
  const messagesItems: MenuProps['items'] = messagesLoading
    ? [
        {
          key: 'messages-loading',
          disabled: true,
          label: (
            <div className="py-4 text-center">
              <Spin size="small" />
            </div>
          ),
        },
      ]
    : messagesErrorItems
      ? messagesErrorItems
      : messages.length > 0
        ? [
            ...messages.map((message) => ({
              key: `message-${message.id}`,
              onClick: () => handleMessageClick(message.customer_id),
              label: (
                <div className="py-2">
                  <div className="font-semibold">{message.customer_name}</div>
                  <div className="text-sm text-gray-600">{message.text}</div>
                  <div className="text-xs text-gray-500">
                    {dayjs(message.created_at).format('DD.MM.YYYY HH:mm')}
                  </div>
                </div>
              ),
            })),
            { type: 'divider' },
            {
              key: 'messages-all',
              label: (
                <div className="text-center text-blue-600">
                  {t('appHeader.messages.viewAll')}
                </div>
              ),
              onClick: () => navigate('/messages/inbox'),
            },
          ]
        : [
            {
              key: 'messages-empty',
              disabled: true,
              label: (
                <div className="py-4">
                  <Empty
                    description={t('appHeader.messages.empty')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              ),
            },
          ];

  const regionErrorItems = buildErrorMenuItems('regions', regionsError);
  const regionItems: MenuProps['items'] = regionsLoading
    ? [
        {
          key: 'regions-loading',
          disabled: true,
          label: (
            <div className="py-4 text-center">
              <Spin size="small" />
            </div>
          ),
        },
      ]
    : regionErrorItems
      ? regionErrorItems
      : regions.map((region) => ({
          key: String(region.id),
          onClick: () => changeRegion(region.id),
          label: (
            <div className="flex items-center justify-between gap-3 py-1">
              <span>{region.name}</span>
              {region.id === currentRegionId && <CheckOutlined className="text-blue-500" />}
            </div>
          ),
        }));

  const customPagesErrorItems = buildErrorMenuItems('customPages', customPagesError);
  const customPagesItems: MenuProps['items'] =
    customPages.length > 0
      ? customPages.map((page) => ({
          key: `custom-${page.id}`,
          icon: <AppstoreAddOutlined />,
          label: page.name,
          onClick: () => {
            if (page.is_external) {
              window.open(page.url, '_blank', 'noopener,noreferrer');
            } else {
              navigate(page.url);
            }
          },
        }))
      : customPagesErrorItems ?? [];

  // Меню профиля пользователя
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('appHeader.profile.my'),
      onClick: () => user && navigate(`/users/${user.id}`),
    },
    {
      key: 'change-password',
      icon: <KeyOutlined />,
      label: t('auth:changePassword'),
      onClick: () => setPasswordModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth:logout'),
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <>
    <div
      className="bg-white dark:bg-gray-800 flex items-center justify-between shadow-md"
      style={{
        height: '52px',        // ✅ КОМПАКТНОСТЬ: было 64px
        padding: '0 16px',     // ✅ КОМПАКТНОСТЬ: было 24px
        position: 'sticky',
        top: 0,
        zIndex: 1,
        width: '100%',
      }}
    >
      {/* Левая часть - кнопка сворачивания/меню и поиск */}
      <div className="flex items-center gap-3 flex-1">
        {isMobile ? (
          /* Кнопка мобильного меню */
          <Button
            type="text"
            icon={<MenuUnfoldOutlined />}
            onClick={onMobileMenuToggle}
            aria-label={t('appHeader.navigation.open')}
            style={{ fontSize: '20px' }}
          />
        ) : (
          /* Кнопка сворачивания десктопного меню */
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={onCollapse}
            aria-label={t(collapsed ? 'appHeader.navigation.expand' : 'appHeader.navigation.collapse')}
            style={{ fontSize: '18px' }}
          />
        )}

        {!isMobile && <GlobalSearch />}
      </div>

      {/* Правая часть - иконки и меню */}
      <Space size="small">
        {/* Быстрые действия - только на десктопе */}
        {!isMobile && <QuickActions />}

        {/* Custom Pages - только на десктопе */}
        {!isMobile && customPagesItems.length > 0 && (
          <Dropdown menu={{ items: customPagesItems }} trigger={['click']}>
            <Tooltip title={t('appHeader.customPages.title')}>
              <Button
                type="text"
                loading={customPagesLoading}
                icon={<AppstoreAddOutlined style={{ fontSize: '20px' }} />}
                aria-label={t('appHeader.customPages.title')}
              />
            </Tooltip>
          </Dropdown>
        )}

        {/* Переключатель языка - скрыт на мобильных */}
        {!isMobile && (
          <Tooltip title={t('appHeader.language.title')}>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              aria-label={t('appHeader.language.title')}
              style={{ width: 75 }}
              size="middle"
              options={[
                { value: 'ru', label: '🇷🇺 RU' },
                { value: 'en', label: '🇬🇧 EN' },
              ]}
            />
          </Tooltip>
        )}

        {/* Переключатель темы */}
        <Tooltip
          title={t(theme === 'dark' ? 'appHeader.theme.switchToLight' : 'appHeader.theme.switchToDark')}
        >
          <Switch
            checked={theme === 'dark'}
            onChange={handleThemeChange}
            aria-label={t('appHeader.theme.control')}
            checkedChildren={<MoonOutlined />}
            unCheckedChildren={<SunOutlined />}
            size={isMobile ? 'small' : 'default'}
          />
        </Tooltip>

        {/* Статус WebSocket */}
        <Tooltip
          title={
            isConnected
              ? t('appHeader.websocket.online')
              : t('appHeader.websocket.offline')
          }
        >
          <span
            className="flex items-center px-1"
            role="status"
            aria-label={isConnected ? t('appHeader.websocket.online') : t('appHeader.websocket.offline')}
          >
            <Badge status={isConnected ? 'success' : 'error'} />
          </span>
        </Tooltip>

        {/* Уведомления */}
        <Dropdown menu={{ items: notificationItems }} trigger={['click']}>
          <Badge count={notificationsUnreadCount} size="small" showZero>
            <Button
              type="text"
              loading={notificationsLoading}
              icon={<BellOutlined style={{ fontSize: isMobile ? '18px' : '20px' }} />}
              aria-label={t('appHeader.notifications.control', { unreadCount: notificationsUnreadCount })}
            />
          </Badge>
        </Dropdown>

        {/* Сообщения */}
        <Dropdown menu={{ items: messagesItems }} trigger={['click']}>
          <Badge count={messagesUnreadCount} size="small" showZero>
            <Button
              type="text"
              loading={messagesLoading}
              icon={<MessageOutlined style={{ fontSize: isMobile ? '18px' : '20px' }} />}
              aria-label={t('appHeader.messages.control', { unreadCount: messagesUnreadCount })}
            />
          </Badge>
        </Dropdown>

        {/* Выбор региона - скрыт на мобильных */}
        {!isMobile && (
          <Dropdown menu={{ items: regionItems }} trigger={['click']}>
            <Tooltip title={t('appHeader.regions.title')}>
              <Button
                type="text"
                loading={regionsLoading}
                icon={<EnvironmentOutlined style={{ fontSize: '20px' }} />}
                aria-label={t('appHeader.regions.title')}
              />
            </Tooltip>
          </Dropdown>
        )}

        {/* Справка - скрыта на мобильных */}
        {!isMobile && (
          <Tooltip title={t('appHeader.help')}>
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: '20px' }} />}
              onClick={() => window.open('https://docs.workernet.local', '_blank')}
              aria-label={t('appHeader.help')}
            />
          </Tooltip>
        )}

        {/* Профиль пользователя */}
        <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
          <button
            type="button"
            aria-label={t('appHeader.profile.menu')}
            className="flex items-center gap-2 cursor-pointer border-0 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700 px-3 py-1 rounded"
          >
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{ backgroundColor: themeContext.token.colorPrimary }}
            />
            {!isMobile && (
              <span className="font-medium text-gray-900 dark:text-white">
                {user?.full_name || user?.username}
              </span>
            )}
          </button>
        </Dropdown>
      </Space>
    </div>
    <SelfPasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
    </>
  );
}
